/**
 * AP-40 7.2 — **Dashboard der Kalkulation**, die Startseite des Hauptmenüpunkts
 * (`/calculations/dashboard`). Jede Kachel führt in die Liste mit denselben Filtern (6.4); die
 * Zahlen rechnet der Server in einem Aufruf, 60 s zwischengespeichert.
 */
import { Link } from 'react-router-dom'
import { Calculator } from 'lucide-react'
import { geldText } from '@symphenia/shared-types'
import { DashboardKopf, DashboardListe, Kachel, KachelRaster, LeerHinweis, useDashboard } from '@/components/dashboard'
import { fmtDate } from '@/lib/format-date'
import { pageWide } from '@/lib/form-styles'
import { fetchCalculationDashboard } from '../api/calculations.api'
import { STATUS_ANZEIGE } from '../lib/kalkulation-labels'
import type { CalculationStatus } from '../types/calculation.types'

interface NaechsteAbgabe {
  id: string
  label: string
  name: string
  siteName: string | null
  dueAt: string | null
  ownerName: string | null
  overdue: boolean
  price?: number
}

interface ZuletztBearbeitet {
  id: string
  label: string
  name: string
  status: CalculationStatus
  updatedAt: string
}

const prozent = (x: unknown) => (typeof x === 'number' ? `${x.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %` : '—')

export default function CalculationDashboardPage() {
  const { bereich, setBereich, daten, fehler, kachel } = useDashboard('calculations', fetchCalculationDashboard)
  const naechste = daten?.lists?.nextDue as NaechsteAbgabe[] | undefined
  const zuletzt = daten?.lists?.recent as ZuletztBearbeitet[] | undefined
  const quote = kachel('hitRate')
  const material = kachel('materialRequests')
  const nichtsOffen = daten && !kachel('inProgress')?.count && !kachel('submitted')?.count

  return (
    <div className={pageWide}>
      <DashboardKopf
        symbol={Calculator}
        titel="Kalkulation"
        untertitel="Was ansteht, was draußen ist und wie es ausgeht."
        bereich={bereich}
        onBereich={setBereich}
        umschalter={daten?.scopeSwitch !== false}
        beschriftung={{ all: 'alle', mine: 'meine' }}
        stand={daten?.stand}
      />

      {fehler && <div className="text-sm text-destructive">{fehler}</div>}

      <KachelRaster>
        <Kachel titel="In Arbeit" kachel={kachel('inProgress')} />
        <Kachel titel="Abgabe in 7 Tagen" ton="warnung" kachel={kachel('dueSoon')} />
        <Kachel titel="Überfällig" ton="gefahr" kachel={kachel('overdue')} hinweis="Abgabetermin vorbei, noch in Arbeit" />
        <Kachel
          titel="Abgegeben, offen"
          kachel={kachel('submitted')}
          hinweis={kachel('submitted')?.amount != null ? `Angebotssumme ${geldText(kachel('submitted')!.amount as number)}` : undefined}
        />
        {quote && (
          <Kachel
            titel="Trefferquote 12 Monate"
            kachel={quote}
            hinweis={
              quote.empty
                ? 'noch nichts entschieden'
                : `${prozent(quote.count)} · ${quote.won as number} gewonnen, ${quote.lost as number} verloren${quote.byAmount != null ? ` · nach Summe ${prozent(quote.byAmount)}` : ''}`
            }
          />
        )}
        <Kachel
          titel="Materialanfragen"
          kachel={material}
          hinweis={material ? `${material.open as number} offen · ${material.requested as number} angefragt (${material.overdue as number} überfällig) · ${material.offersNotApplied as number} Angebote nicht übernommen` : undefined}
        />
        <Kachel titel="KI-Hinweise" kachel={kachel('aiHints')} hinweis="offene Befunde der Prüfung" />
      </KachelRaster>

      {nichtsOffen && (
        <LeerHinweis
          verweise={[
            { zu: '/calculations', text: 'Kalkulationen' },
            { zu: '/calculations/templates', text: 'Vorlagen' },
          ]}
        />
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <DashboardListe
          titel="Nächste Abgabetermine"
          zeilen={naechste}
          schluessel={(k) => k.id}
          alle="/calculations?status=IN_PROGRESS"
          leer="Keine Kalkulation mit Abgabetermin in Arbeit."
          zeile={(k) => (
            <>
              <span className={`w-20 shrink-0 text-xs ${k.overdue ? 'font-semibold text-destructive' : 'text-muted-foreground'}`}>{k.dueAt ? fmtDate(k.dueAt) : ''}</span>
              <Link to={`/calculations/${k.id}`} className="min-w-0 flex-1 truncate hover:underline">
                <span className="mr-2 font-mono text-xs text-muted-foreground">{k.label}</span>
                {k.name}
                {k.siteName && <span className="text-muted-foreground"> · {k.siteName}</span>}
              </Link>
              <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">{k.ownerName}</span>
              {k.price != null && <span className="w-24 shrink-0 text-right text-xs tabular-nums">{geldText(k.price)}</span>}
            </>
          )}
        />
        <DashboardListe
          titel="Zuletzt bearbeitet (meine)"
          zeilen={zuletzt}
          schluessel={(k) => k.id}
          leer="Noch keine eigene Kalkulation."
          zeile={(k) => (
            <>
              <Link to={`/calculations/${k.id}`} className="min-w-0 flex-1 truncate hover:underline">
                <span className="mr-2 font-mono text-xs text-muted-foreground">{k.label}</span>
                {k.name}
              </Link>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${STATUS_ANZEIGE[k.status]?.klasse ?? ''}`}>{STATUS_ANZEIGE[k.status]?.label ?? k.status}</span>
            </>
          )}
        />
      </div>
    </div>
  )
}
